import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { API_TAGS, createLazyQuery } from '@/shared/api';
import {
  fetchAvgByAssessment,
  fetchAvgRating,
  fetchChartSchema,
  fetchConversationPerDay,
  fetchCustomChartData,
  fetchEvaluationComments,
  fetchIndicatorConversations,
  fetchMissellingData,
  fetchOperatorStats,
  fetchQualityDetails,
  fetchQualitySummary,
  fetchQualityHeatmap,
  fetchQualityConsultantReport,
  resolveChartRule,
  signChartRule,
} from './api';
import {
  CustomChartDataParams,
  IndicatorConversationsParams,
  MissellingDataParams,
  QualityDetailsParams,
  QualitySummaryParams,
  QualityHeatmapParams,
  QualityConsultantReportParams,
} from './types';

export const dashboardKeys = {
  all: [API_TAGS.DASHBOARDS] as const,
  avgByAssessment: (params?: { limit?: number; offset?: number }) =>
    [...dashboardKeys.all, 'avgByAssessment', { ...params }] as const,
  avgRating: (params?: { 'date-from'?: string | null; 'date-to'?: string | null }) =>
    [...dashboardKeys.all, 'avgRating', { ...params }] as const,
  conversationPerDay: (params?: { 'date-from'?: string | null; 'date-to'?: string | null }) =>
    [...dashboardKeys.all, 'conversationPerDay', { ...params }] as const,
  operator: (params?: { limit?: number; offset?: number }) =>
    [...dashboardKeys.all, 'operator', { ...params }] as const,
};

export const useAvgByAssessment = (params?: { limit?: number; offset?: number }) => {
  return useQuery({
    queryKey: dashboardKeys.avgByAssessment(params),
    queryFn: () => fetchAvgByAssessment(params),
  });
};

export const useAvgRating = (params?: { 'date-from'?: string | null; 'date-to'?: string | null }) => {
  return useQuery({
    queryKey: dashboardKeys.avgRating(params),
    queryFn: () => fetchAvgRating(params),
  });
};

export const useConversationPerDay = (params?: { 'date-from'?: string | null; 'date-to'?: string | null }) => {
  return useQuery({
    queryKey: dashboardKeys.conversationPerDay(params),
    queryFn: () => fetchConversationPerDay(params),
  });
};

export const useOperatorStats = (params?: { limit?: number; offset?: number }) => {
  return useQuery({
    queryKey: dashboardKeys.operator(params),
    queryFn: () => fetchOperatorStats(params),
  });
};

export const useQualityDetails = (params?: QualityDetailsParams, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [...dashboardKeys.all, 'qualityDetails', { ...params }] as const,
    queryFn: () => fetchQualityDetails(params),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
};

/**
 * Порции таблицы «Все детали»: окно выдачи запрашивается по мере прокрутки, а не одним куском.
 *
 * Ленивый, а не обычный запрос: окно задаёт сама таблица по тому, до чего пользователь докрутил,
 * — момент обращения известен ей, а не рендеру. Ключ кэша тот же, что у {@link useQualityDetails},
 * поэтому уже полученное окно с теми же условиями второй раз к бэкенду не идёт.
 */
export const useLazyQualityDetails = createLazyQuery(
  (params: QualityDetailsParams) => [...dashboardKeys.all, 'qualityDetails', { ...params }] as const,
  fetchQualityDetails,
);

export const useQualitySummary = (params?: QualitySummaryParams) => {
  return useQuery({
    queryKey: [...dashboardKeys.all, 'qualitySummary', { ...params }] as const,
    queryFn: () => fetchQualitySummary(params),
    placeholderData: keepPreviousData,
  });
};

export const useQualityHeatmap = (params?: QualityHeatmapParams) => {
  return useQuery({
    queryKey: [...dashboardKeys.all, 'qualityHeatmap', { ...params }] as const,
    queryFn: () => fetchQualityHeatmap(params),
    placeholderData: keepPreviousData,
  });
};

/**
 * Диалоги одного показателя сводки — запрашиваются в момент перехода, а не при загрузке
 * дашборда: перечни росли вместе с выборкой, а нужен всегда один — того показателя, по
 * которому кликнули.
 *
 * Ленивый, а не обычный запрос: состав нужен ровно однажды, ответом на клик. Повторный
 * переход по тому же показателю с теми же фильтрами берётся из кэша — второго обращения
 * к бэкенду он не делает.
 */
export const useLazyIndicatorConversations = createLazyQuery(
  (params: IndicatorConversationsParams) => [...dashboardKeys.all, 'indicatorConversations', { ...params }] as const,
  fetchIndicatorConversations,
);

/**
 * Комментарии по блокам одной открытой оценки. `id === null` — карточка звонка закрыта,
 * запрос не уходит: список оценок комментариев не несёт, и нужны они только открытой карточке.
 */
export const useEvaluationComments = (id: number | null) => {
  return useQuery({
    queryKey: [...dashboardKeys.all, 'evaluationComments', id] as const,
    queryFn: () => fetchEvaluationComments({ id: id! }),
    enabled: id !== null,
  });
};

export const useQualityConsultantReport = (params: QualityConsultantReportParams | null) => {
  return useQuery({
    queryKey: [...dashboardKeys.all, 'qualityConsultantReport', { ...params }] as const,
    queryFn: () => fetchQualityConsultantReport(params!),
    enabled: !!params,
    placeholderData: keepPreviousData,
  });
};

/**
 * Хук для получения данных дашборда мисселинга «РГС Жизнь».
 * При смене фильтров повторяет запрос; использует keepPreviousData для плавной смены.
 */
export const useMissellingData = (params?: MissellingDataParams) => {
  return useQuery({
    queryKey: [...dashboardKeys.all, 'missellingData', { ...params }] as const,
    queryFn: () => fetchMissellingData(params),
    placeholderData: keepPreviousData,
  });
};

/**
 * Истолкование описания диаграммы в правило отбора.
 *
 * Мутация, а не запрос: описание истолковывается один раз — при сохранении диаграммы, — и
 * закрепляется за ней. Показ дашборда к модели уже не обращается.
 */
export const useResolveChartRule = () => {
  return useMutation({
    mutationFn: resolveChartRule,
  });
};

/**
 * Заверение запроса диаграммы, правленного пользователем вручную.
 *
 * Мутация, а не запрос: заверение — действие сохранения правки. Описание диаграммы при этом
 * заново не истолковывается — правленый запрос принимается как есть.
 */
export const useSignChartRule = () => {
  return useMutation({
    mutationFn: signChartRule,
  });
};

/**
 * Справочник витрины данных: перечень представлений с колонками, атрибуты и заключения.
 * Запрашивается только при разрешении на правку запроса — прочим справочник не отдаётся.
 */
export const useChartSchema = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [...dashboardKeys.all, 'chartSchema'] as const,
    queryFn: fetchChartSchema,
    enabled: options?.enabled ?? true,
    // Состав витрины меняется вместе с настройками системы, а не по ходу правки диаграммы.
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Данные одной диаграммы пользовательского дашборда по её правилу и фильтрам панели.
 * `params === null` — диаграммы нет (пресет не применён), запрос не отправляется.
 */
export const useCustomChartData = (params: CustomChartDataParams | null) => {
  return useQuery({
    queryKey: [...dashboardKeys.all, 'customChartData', { ...params }] as const,
    queryFn: () => fetchCustomChartData(params!),
    enabled: !!params,
    placeholderData: keepPreviousData,
  });
};
